import { getFacebookLocale } from '@change-corgi/config/facebook';
import { stripHtmlTags } from '@change-corgi/core/html';
import type { I18n } from '@change-corgi/core/i18n';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import type { PageMetadata } from '#src/shared/prefetch';

import { addSchemeToUrl } from '#src/app/shared/utils/assets';
import { isLoaded } from '#src/app/shared/utils/async';
import { stripEmojis, truncate } from '#src/app/shared/utils/string';

import { petitionDetailsIsIndexed } from './pageCanonical';
import type { PetitionDetailsMetaContext, PetitionDetailsPageHeadData, ShareHeadlineOverrides } from './types';

// eslint-disable-next-line max-lines-per-function
export function petitionDetailsPageMetadata({
	prefetchedData,
	i18n,
	getStaticAssetUrl,
	metaContext,
	bestShareVisual,
	shareHeadlineOverrides,
}: {
	prefetchedData: PetitionDetailsPageHeadData;
	i18n: I18n | UtilityContext['i18n'];
	getStaticAssetUrl: (pathname: string) => string;
	metaContext: PetitionDetailsMetaContext;
	bestShareVisual?: boolean;
	shareHeadlineOverrides?: ShareHeadlineOverrides;
}): PageMetadata | undefined {
	if (!isLoaded(prefetchedData.pageData)) {
		return undefined;
	}

	const { petition } = prefetchedData.pageData.pageData;
	const indexed = petitionDetailsIsIndexed(prefetchedData);

	const description = petition.description
		? truncate(stripEmojis(stripHtmlTags(petition.description)), 400)
		: undefined;

	const { facebookNamespace, ogUrlPath } = metaContext;
	const ogType = facebookNamespace ? (`${facebookNamespace}:petition` as const) : undefined;

	const ogLocale = getFacebookLocale(petition.originalLocale.localeCode) || getFacebookLocale('en-US');

	const { signatureState, status, ask } = petition;
	const { signatureCount } = signatureState;

	const { ogTitle, ogDescription } = (() => {
		if (status === 'VICTORY') {
			return {
				ogTitle: i18n.translatePlural('fe.facebook.share.victory', signatureCount.displayed, {
					signature_count: signatureCount.displayedLocalized,
				}),
				ogDescription: ask,
			};
		}
		if (shareHeadlineOverrides?.title) {
			return {
				ogTitle: shareHeadlineOverrides.title,
				ogDescription: shareHeadlineOverrides.description || ask,
			};
		}
		return {
			ogTitle: i18n.translate('fe.facebook.share.sign_petition'),
			ogDescription: ask,
		};
	})();

	const defaultPhoto = { url: getStaticAssetUrl('/images/default-petition.gif'), width: 1120, height: 630 };

	const metaPhoto = (() => {
		if (shareHeadlineOverrides?.imageUrl) {
			return { ...defaultPhoto, url: shareHeadlineOverrides.imageUrl };
		}
		if (bestShareVisual && petition.bestShareVisual?.url) {
			return { ...defaultPhoto, url: petition.bestShareVisual.url };
		}
		return petition.photo?.petitionLarge || defaultPhoto;
	})();

	const ogImage = addSchemeToUrl(metaPhoto.url);

	return {
		description,
		...(!indexed && { robots: 'noindex' as const }),
		// FIXME `${facebookNamespace}:petition` does not appear to be a valid value for ogType
		// so we're casting it to 'article' to satisfy the type checker
		...(ogType && { ogType: ogType as unknown as 'article' }),
		...(ogLocale && { ogLocale }),
		ogUrl: ogUrlPath,
		ogTitle,
		ogDescription,
		ogImage,
		ogImageSecureUrl: ogImage,
		ogImageWidth: metaPhoto.width,
		ogImageHeight: metaPhoto.height,
		twitterCard: 'summary_large_image' as const,
		twitterImage: ogImage,
		twitterImageAlt: ogTitle,
	};
}
